// @ts-check

/**
 * d-recipe-share
 * Botão de compartilhar do banner de receita (sections/d-revenues-banner.liquid).
 *
 * Usa a API de compartilhamento do aparelho quando existe. Quando não existe
 * (a maioria dos navegadores de computador), copia o link e mostra o aviso na
 * tela, sem janela de alerta.
 *
 * Ouve o clique por classe, num único listener no documento, então:
 *   • funciona com quantas instâncias da seção houver na página;
 *   • funciona para botões que entram depois (recarga de seção no editor);
 *   • nunca quebra quando a seção não tem botão de compartilhar.
 *
 * Markup esperado:
 *   <button class="d-rb__share-btn"
 *           data-share-title="…" data-share-text="…" data-share-url="…">
 *   <span data-share-feedback role="status" aria-live="polite"></span>
 */

const BUTTON_SELECTOR = '.d-rb__share-btn';
const GROUP_SELECTOR = '.d-rb__buttons';
const FEEDBACK_TIME = 2500;

/** Timer de cada aviso, para um clique novo não apagar o aviso antes da hora. */
/** @type {WeakMap<HTMLElement, number>} */
const timers = new WeakMap();

/**
 * Mostra um aviso curto ao lado do grupo de botões.
 * @param {HTMLElement} button
 * @param {string} message
 */
function showFeedback(button, message) {
  const group = button.closest(GROUP_SELECTOR);
  const feedback = group?.querySelector(':scope > [data-share-feedback]');

  if (!(feedback instanceof HTMLElement)) return;

  feedback.textContent = message;
  feedback.setAttribute('data-visible', '');

  const running = timers.get(feedback);
  if (running) clearTimeout(running);

  timers.set(
    feedback,
    window.setTimeout(() => {
      feedback.removeAttribute('data-visible');
      timers.delete(feedback);
    }, FEEDBACK_TIME)
  );
}

/**
 * Copia o link e avisa na tela. É a alternativa para quem não tem a API de
 * compartilhamento do aparelho.
 * @param {HTMLElement} button
 * @param {string} url
 */
async function copyLink(button, url) {
  try {
    await navigator.clipboard.writeText(url);
    showFeedback(button, 'Link copiado');
  } catch {
    showFeedback(button, 'Não foi possível copiar o link');
  }
}

/**
 * @param {MouseEvent} event
 */
async function onClick(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const button = target.closest(BUTTON_SELECTOR);
  if (!(button instanceof HTMLElement)) return;

  const url = button.dataset.shareUrl || window.location.href;
  const title = button.dataset.shareTitle || document.title;
  const text = button.dataset.shareText || '';

  if (!navigator.share) {
    await copyLink(button, url);
    return;
  }

  try {
    await navigator.share({ title, text, url });
  } catch (error) {
    // Cancelar o compartilhamento é uso normal, não erro: não avisa nada.
    if (error instanceof DOMException && error.name === 'AbortError') return;
    await copyLink(button, url);
  }
}

// O módulo já roda uma vez só por URL, mas a trava deixa explícito que não
// existe listener duplicado nem que outro arquivo importe isto de novo.
const scope = /** @type {Window & { dRecipeShareBound?: boolean }} */ (window);

if (!scope.dRecipeShareBound) {
  scope.dRecipeShareBound = true;
  document.addEventListener('click', onClick);
}
